import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useLoader } from '../hooks/loader';

export const EmptyArrayCheck = <T extends Array<unknown>>(data: T): boolean => data.length === 0;
export type CacheReloadPolicy = 'once' | 'never' | 'always';
export type CacheReadFn<T> = () => Promise<T>;
export type CacheReadWithKeyFn<T> = (key: string) => Promise<T>;
export type CacheLoadFn<T> = (blocking?: boolean, refreshCache?: CacheReloadPolicy) => Promise<T>;
export type CacheLoadFnWithKey<T> = (key: string, blocking?: boolean, refreshCache?: CacheReloadPolicy) => Promise<T>;

export function defineCacheStore<T>(
  name: string,
  initialValue: T,
  readFromDb: CacheReadFn<T>,
  isEmpty: (data: T) => boolean
) {
  return defineStore(
    name,
    () => {
      const cache = ref<T>(initialValue);
      const { wrapPromiseFn, state, reset } = useLoader();

      function setCache(value: T) {
        cache.value = value;
      }

      function clearCache() {
        console.log(`[store.cache] [${name}] clearCache called. Setting cache to initial value.`, initialValue);
        cache.value = initialValue;
        reset();
      }

      const loadAndUpdateCache: CacheLoadFn<T> = async function loadAndUpdateCache(
        blocking = false,
        refreshCache: CacheReloadPolicy = 'once'
      ): Promise<T> {
        console.log(`[store.cache] [${name}] loadAndUpdateCache called with blocking=${blocking}`);
        if (blocking || isEmpty(cache.value)) {
          console.log(
            `[store.cache] [${name}] [blocking=${blocking}, isEmpty=${isEmpty(cache.value)}] data is empty or blocking is true`
          );
          return wrapPromiseFn(readFromDb)().then((data: T) => {
            cache.value = data;
            console.log(`[store.cache] [${name}] data loaded`, data);
            return data;
          });
        } else {
          const p = Promise.resolve(cache.value as T);
          if (refreshCache === 'always' || (refreshCache === 'once' && !state.finished)) {
            console.log(
              `[store.cache] [${name}] refreshing cache in background, refreshCache=${refreshCache}, state.finished=${state.finished}`
            );

            wrapPromiseFn(readFromDb)().then((data) => {
              console.log(`[store.cache] [${name}] background data loaded`);
              cache.value = data;
            });
          } else {
            console.log(
              `[store.cache] [${name}] not refreshing cache, refreshCache=${refreshCache}, state.finished=${state.finished}`
            );
          }
          return p;
        }
      };

      return {
        cache,
        setCache,
        clearCache,
        loadAndUpdateCache,
        state,
      };
    },
    {
      persist: {
        pick: ['cache'],
      },
    }
  );
}

export function defineArrayCacheStore<T>(name: string, initialValue: T[], readFromDb: () => Promise<T[]>) {
  return defineCacheStore<T[]>(name, initialValue, readFromDb, (data) => EmptyArrayCheck(data));
}

export function defineObjectCacheStore<T>(
  name: string,
  initialValue: Record<string, T>,
  readFromDb: CacheReadWithKeyFn<T>
) {
  return defineStore(
    name,
    () => {
      const cache = ref<Record<string, T>>(initialValue);
      const { wrapPromiseFn, state } = useLoader();

      function setCache(value: Record<string, T>) {
        cache.value = value;
      }

      function clearCache() {
        cache.value = initialValue;
      }

      function setItem(key: string, value: T) {
        cache.value = { ...cache.value, [key]: value };
      }

      function removeItem(key: string) {
        cache.value = {
          ...Object.fromEntries(Object.entries(cache.value).filter(([k]) => k !== key)),
        };
      }

      function getItem(key: string): T | undefined {
        return cache.value[key];
      }

      const loadAndUpdateCache: CacheLoadFnWithKey<T> = async function loadAndUpdateCache(
        key: string,
        blocking = false,
        refreshCache: CacheReloadPolicy = 'once'
      ): Promise<T> {
        if (blocking || key! in cache.value || cache.value[key] === undefined) {
          console.log(`[store.cache] [${name}] [key=${key}] data is empty or blocking is true`);
          return wrapPromiseFn(() => readFromDb(key))().then((data: T) => {
            cache.value = { ...cache.value, [key]: data };
            return data;
          });
        } else {
          const p = Promise.resolve(cache.value[key] as T);
          if (refreshCache === 'always' || (refreshCache === 'once' && !state.finished)) {
            console.log(
              `[store.cache] [${name}] [key=${key}] refreshing cache in background, refreshCache=${refreshCache}, state.finished=${state.finished}`
            );
            wrapPromiseFn(() => readFromDb(key))().then((data) => {
              cache.value = { ...cache.value, [key]: data };
            });
          } else {
            console.log(
              `[store.cache] [${name}] [key=${key}] not refreshing cache, refreshCache=${refreshCache}, state.finished=${state.finished}`
            );
          }

          return p;
        }
      };

      return {
        cache,
        setCache,
        clearCache,
        loadAndUpdateCache,
        state,
        setItem,
        removeItem,
        getItem,
      };
    },
    {
      persist: true,
    }
  );
}
